import React, { useState } from "react";

import { Box, Button, Container, Typography } from "@material-ui/core";
import clsx from "clsx";

import MuiPhoneNumber from "material-ui-phone-number";
import { useTranslation } from "react-i18next";

import PageTitle from "components/PageTitle";
import PlacesAutocomplete from "components/PlacesAutocomplete";
import TextField from "components/TextField";
import { AvailableOrderTypes } from "gql/types/globals";
import makeStyles from "hooks/makeStyles";
import useBusiness from "hooks/useBusiness";
import useCart from "hooks/useCart";
import useForm from "hooks/useForm";
import ShoppingBagIcon from "icons/ShoppingBagIcon";
import SignOutAltIcon from "icons/SignOutAltIcon";
import UserIcon from "icons/UserIcon";
import { formatMoney } from "utility/formatMoney";

import validate from "./validate";

const useStyles = makeStyles(theme => ({
	root: {
		padding: 0,
		display: "flex",
		flex: 1,
		flexDirection: "column",
		// height: "calc(100vh - 95px)", // not great..
		height: "calc(var(--vh, 1vh) * 100 - 95px)",
		position: "relative",
		[theme.breakpoints.up(480)]: {
			overflow: "hidden",
			height: "100vh"
		}
	},
	body: {
		[theme.breakpoints.up(480)]: {
			height: "auto",
			backgroundColor: theme.palette.background.default,
			color: theme.palette.text.primary,
			borderBottomLeftRadius: theme.spacing(2),
			borderBottomRightRadius: theme.spacing(2),
			padding: theme.spacing(4)
		}
	},
	title: {
		alignSelf: "flex-start",
		textAlign: "left",
		textTransform: "uppercase",
		fontWeight: 500,
		fontSize: 16,
		letterSpacing: "0.38px"
	},
	subTitle: {
		fontSize: 12
	},
	items: {
		marginBottom: theme.spacing(2)
	},
	scroll: {
		//height: "calc(100vh - 195px)",
		height: "calc(var(--vh, 1vh) * 100 - 195px)",
		position: "relative",
		alignItems: "center",
		overflowY: "scroll",
		[theme.breakpoints.up(480)]: {
			height: "calc(100vh - 250px)",
			overflowY: "auto"
		}
	},
	scrollBox: {
		display: "flex",
		flexDirection: "column"
	},
	itemDivider: {
		marginTop: theme.spacing(2),
		backgroundColor: "#f2f2f2"
	},
	item: {
		marginLeft: theme.spacing(2),
		marginTop: theme.spacing(2),
		marginRight: theme.spacing(2),
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "space-between"
	},
	itemTitle: {
		color: "#f2f2f2",
		textTransform: "uppercase",
		fontSize: "12px",
		letterSpacing: "0.38px"
	},
	itemTitleChecked: {
		color: "#fff"
	},
	titleRow: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		[theme.breakpoints.up(480)]: {
			marginTop: theme.spacing(2),
			marginBottom: theme.spacing(2)
		}
	},
	icon: {
		marginRight: 10,
		fill: "#fff",
		[theme.breakpoints.up(480)]: {
			fill: theme.palette.text.primary
		}
	},
	form: {
		padding: 0
	},

	phoneField: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		borderColor: "#fff",
		border: "none",
		color: "#000",
		backgroundColor: "#f2f2f2",
		borderRadius: 8
	},
	footer: {
		backgroundColor: "transparent",
		bottom: 0,
		left: 0,
		right: 0,
		width: "100%",
		marginTop: theme.spacing(1),
		marginRight: theme.spacing(1),
		padding: 0,
		position: "absolute",
		minHeight: "45px",
		[theme.breakpoints.up(480)]: {
			marginTop: 0,
			width: "auto",
			bottom: "6px",
			flex: "1",
			justifyContent: "center",
			padding: theme.spacing(2)
		}
	},
	textField: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		borderColor: "#fff",
		color: "#000",
		backgroundColor: "#f2f2f2",
		borderRadius: 8
	},
	addButton: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		borderRadius: 24,
		fontSize: 16,
		height: 48,
		width: "100%",
		padding: 0,
		alignContent: "center",
		justifyContent: "center",
		textTransform: "uppercase",
		"&:hover": {
			[theme.breakpoints.up(480)]: {
				backgroundColor: theme.palette.primary.main,
				color: theme.palette.text.primary
			}
		}
	},
	buttonIcon: {
		position: "absolute",
		right: 10
	},
	actionButtonLabel: {
		marginLeft: theme.spacing(0.5)
	}
}));

interface Props {
	onChange: () => void;
}

const CustomerInfoPanel = ({ onChange }: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { business } = useBusiness();
	const { orderType, cartTotalCents, fullName, phoneNumber, email, deliveryAddress, saveCustomerInfo } = useCart();
	const [isCheckoutEnabled, setIsCheckoutEnabled] = useState(true);
	const { values, setValue, errors, onChange: onFormDataChange } = useForm(
		submitCallback,
		{ fullName, phoneNumber, email, deliveryAddress },
		validate
	);

	console.log(orderType);

	const onCheckoutClicked = async e => {
		e.preventDefault();
		const isValid = Object.keys(errors).length === 0;

		if (isValid) {
			//Update the order
			await saveCustomerInfo({
				fullName: values.fullName,
				phoneNumber: values.phoneNumber,
				email: values.email,
				deliveryAddress: values.deliveryAddress
			});
			onChange();
		}
	};

	function submitCallback() {}

	const onPhoneChanged = value => {
		setValue({ phoneNumber: value });
	};

	return (
		<Container className={classes.root}>
			<PageTitle businessName={business.name} subTitle={t("checkout")} />

			<form onSubmit={async e => await onCheckoutClicked(e)}>
				<Box className={classes.body}>
					<Box className={classes.titleRow}>
						<ShoppingBagIcon height={20} width={20} className={classes.icon} />
						<Typography className={classes.title}>{t("placeOrder")}</Typography>
					</Box>

					<Box className={classes.titleRow}>
						<UserIcon className={classes.icon} />
						<Typography className={clsx(classes.title, classes.subTitle)}>{t("customerInformation")}</Typography>
					</Box>

					<Box className={classes.scroll}>
						<Box className={classes.scrollBox}>
							<Box className={classes.form}>
								<TextField
									placeholder={t("fullName")}
									variant="outlined"
									inputProps={{ maxLength: 80 }}
									id="fullName"
									name="fullName"
									required
									value={values.fullName}
									error={errors?.fullName ? true : false}
									onChange={onFormDataChange}
								/>
								{orderType?.orderType !== AvailableOrderTypes.pickup ? (
									<PlacesAutocomplete
										className={classes.textField}
										placeholder={t("deliveryAddress")}
										defaultValue={deliveryAddress?.place || values?.deliveryAddress?.place}
										origin={business?.address?.coordinates}
										radius={orderType?.deliveryRadiusMeters || undefined}
										onChange={(event, value) => {
											setValue({
												deliveryAddress: value && {
													address1: value.structured_formatting.main_text,
													address2: null,
													address3: null,
													city: value.structured_formatting.secondary_text,
													state: null,
													postalCode: null,
													country: null,
													placeId: value.place_id,
													place: value
												}
											});
										}}
									/>
								) : null}
								<MuiPhoneNumber
									className={classes.phoneField}
									inputProps={{ className: classes.phoneField, placeholder: t("mobilePhone") }}
									defaultCountry={"us"}
									onlyCountries={["us"]}
									disableCountryCode
									disableDropdown
									fullWidth
									required
									id="phoneNumber"
									name="phoneNumber"
									value={values.phoneNumber}
									onChange={onPhoneChanged}
									error={errors?.mobilePhone ? true : false}
									variant="outlined"
								/>

								<TextField
									placeholder={t("email")}
									inputMode={"email"}
									id="email"
									name="email"
									value={values.email}
									onChange={onFormDataChange}
									variant="outlined"
									inputProps={{ maxLength: 200 }}
									required
								/>
							</Box>
						</Box>
					</Box>
				</Box>
				<Box className={classes.footer}>
					<Button
						variant="outlined"
						className={classes.addButton}
						type="submit"
						// onClick={onCheckoutClicked}
						disabled={!isCheckoutEnabled}
					>
						{formatMoney(cartTotalCents)}
						<Typography className={classes.actionButtonLabel}>{` - ${t("finalizeOrder")}`}</Typography>
						<SignOutAltIcon className={classes.buttonIcon} />
					</Button>
				</Box>
			</form>
		</Container>
	);
};

export default CustomerInfoPanel;
